import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Play, Plus, Sparkles } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
	PodcastPlaybackProgressService,
	type PodcastPlaybackProgress,
} from "@/lib/podcast/podcastPlaybackProgress.service";
import { getPlayerActions } from "@/lib/player/player.store";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

/** Recent-listen record enriched with the joined podcast metadata the API returns. */
type RecentListen = PodcastPlaybackProgress & {
	podcast?: {
		id: string;
		title: string;
		podWorkspace?: { id: string; title: string } | null;
	} | null;
};

interface ContinueListeningBarProps {
	/** Opens the create-podcast flow (used by the "create next" state). */
	onCreateClick: () => void;
}

function formatRemaining(seconds: number): string {
	const safe = Math.max(0, Math.floor(seconds));
	const mins = Math.floor(safe / 60);
	const secs = safe % 60;
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Habit Loop v1 - dashboard bar with two states:
 *  - Resume: when there is an in-progress (<95%, not completed) listen.
 *  - Create next: when nothing is in progress, nudge the user to make their
 *    next podcast (the real retention bottleneck for this product).
 */
export function ContinueListeningBar({
	onCreateClick,
}: ContinueListeningBarProps) {
	const { t } = useTranslation();
	const { balance } = useCoinBalance();

	const { data, isLoading } = useQuery({
		queryKey: ["playback-progress", "recent", 1],
		queryFn: () => PodcastPlaybackProgressService.getRecentListens(1),
		staleTime: 1000 * 30,
	});

	const recent = (data?.[0] as RecentListen | undefined) ?? null;

	const duration = recent?.duration ?? 0;
	const currentTime = recent?.currentTime ?? 0;
	const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
	const isResumable =
		!!recent && !recent.completed && duration > 0 && pct < 95 && pct > 0;

	// Fire the appropriate "shown" event once per mount/state resolution.
	const shownRef = useRef<string | null>(null);
	useEffect(() => {
		if (isLoading) return;
		const state = isResumable ? "resume" : "create";
		if (shownRef.current === state) return;
		shownRef.current = state;
		if (isResumable) {
			AnalyticsService.track("continue_listening_shown", {
				podcast_id: recent?.podcastId,
			});
		} else {
			AnalyticsService.track("create_next_prompt_shown", {
				source: "dashboard",
			});
		}
	}, [isLoading, isResumable, recent?.podcastId]);

	if (isLoading) return null;

	if (isResumable && recent) {
		const remaining = duration - currentTime;
		const handleResume = () => {
			AnalyticsService.track("continue_listening_clicked", {
				podcast_id: recent.podcastId,
			});
			getPlayerActions().loadPodcast(recent.podcastId, true);
		};

		return (
			<Card className="mb-4 md:mb-6 p-3 md:p-4">
				<div className="flex items-center gap-3 md:gap-4">
					<div className="min-w-0 flex-1">
						<p className="text-xs text-muted-foreground mb-1">
							{t("dashboard.continueListening.title")}
						</p>
						<p className="truncate text-sm md:text-base font-medium text-foreground">
							{recent.podcast?.title ?? t("dashboard.continueListening.title")}
						</p>
						<div className="mt-2 flex items-center gap-2">
							<Progress value={pct} className="h-1.5 flex-1" />
							<span className="shrink-0 text-xs text-muted-foreground">
								{t("dashboard.continueListening.remaining", {
									time: formatRemaining(remaining),
								})}
							</span>
						</div>
					</div>
					<Button onClick={handleResume} size="sm" className="shrink-0 gap-1.5">
						<Play className="h-4 w-4" />
						<span className="hidden sm:inline">
							{t("dashboard.continueListening.cta")}
						</span>
					</Button>
				</div>
			</Card>
		);
	}

	// Create-next state.
	const handleCreate = () => {
		AnalyticsService.track("create_next_prompt_clicked", {
			source: "dashboard",
		});
		onCreateClick();
	};

	return (
		<Card className="mb-4 md:mb-6 p-3 md:p-4">
			<div className="flex items-center gap-3 md:gap-4">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5 text-foreground">
						<Sparkles className="h-4 w-4 text-primary shrink-0" />
						<p className="text-sm md:text-base font-medium">
							{t("dashboard.createNext.title")}
						</p>
					</div>
					<p className="mt-0.5 truncate text-xs md:text-sm text-muted-foreground">
						{balance != null
							? t("dashboard.createNext.subtitleWithBalance", {
									balance,
								})
							: t("dashboard.createNext.subtitle")}
					</p>
				</div>
				<Button onClick={handleCreate} size="sm" className="shrink-0 gap-1.5">
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">
						{t("dashboard.createNext.cta")}
					</span>
				</Button>
			</div>
		</Card>
	);
}
